import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// utils
import { fData } from 'src/utils/format-number';
//
import { useState } from 'react';
import Iconify from '../iconify';
import { varFade } from '../animate';
import FileThumbnail, { fileData } from '../file-thumbnail';

// ----------------------------------------------------------------------

export default function MultiFilePreview({
  thumbnail,
  files,
  onRemove,
  sx,
  onRemoveAll,
  onClick,
  setImageDialog,
  setValue,
}) {
  const [imageError, setImageError] = useState(false);
  const FORMAT_PDF = ['pdf'];
  const FORMAT_TEXT = ['txt'];
  const FORMAT_PHOTOSHOP = ['psd'];
  const FORMAT_WORD = ['doc', 'docx'];
  const FORMAT_EXCEL = ['xls', 'xlsx'];
  const FORMAT_ZIP = ['zip', 'rar', 'iso'];
  const FORMAT_ILLUSTRATOR = ['ai', 'esp'];
  const FORMAT_POWERPOINT = ['ppt', 'pptx'];
  const FORMAT_AUDIO = ['wav', 'aif', 'mp3', 'aac'];
  const FORMAT_IMG = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'svg'];
  const FORMAT_VIDEO = ['m4v', 'avi', 'mpg', 'mp4', 'webm'];
  const FORMAT_SQL = ['sql'];

  if (!Array.isArray(files) || files.some(file => !file)) {
    return null; // Return null if files is invalid or contains invalid items
  }

  return !imageError ? (
    <AnimatePresence initial={false}>
      {files.map((file) => {
        const { key, name = '', size = 0 } = fileData(file);
        const isNotFormatFile = typeof file === 'string';
        const extension = file.path && typeof file.path === 'string' 
          ? file.path.split('.').pop() 
          : null;

        // Check if file is video - allow videos to be displayed
        const isVideo = FORMAT_VIDEO.includes(extension) || (file?.type && file.type.startsWith('video/'));
        
        // Filter out other non-image file types (but allow videos)
        if (
          FORMAT_EXCEL.includes(extension) ||
          FORMAT_PDF.includes(extension) ||
          FORMAT_TEXT.includes(extension) ||
          FORMAT_WORD.includes(extension) ||
          FORMAT_ZIP.includes(extension) ||
          FORMAT_AUDIO.includes(extension) ||
          FORMAT_ILLUSTRATOR.includes(extension) ||
          FORMAT_POWERPOINT.includes(extension) ||
          FORMAT_PHOTOSHOP.includes(extension) || 
          FORMAT_SQL.includes(extension)
        ) {
          if (onRemove) {
            onRemove(file);
          }
          return null;
        }

        // Allow videos and images (exclude xlsx)
        const isImage = FORMAT_IMG.includes(extension) || (file?.type && file.type.startsWith('image/'));
        
        if (thumbnail && !imageError && extension !== 'xlsx' && (isImage || isVideo)) {
          return (
            <Stack
              key={key}
              component={m.div}
              {...varFade().inUp}
              alignItems="center"
              display="inline-flex"
              justifyContent="center"
              sx={{
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.25,
                overflow: 'hidden',
                position: 'relative',
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                ...sx,
              }}
            >
              <FileThumbnail
                onClick={onClick}
                tooltip
                imageView
                file={file}
                capture="environment"
                sx={{ position: 'absolute' }}
                imgSx={{ position: 'absolute' }}
                imageError={imageError}
                setImageError={setImageError}
                setValue={setValue}
              />

              {onRemove && (
                <IconButton
                  size="small"
                  onClick={() => {
                    onRemove(file);
                  }}
                  sx={{
                    p: 0.5,
                    top: 4,
                    right: 4,
                    position: 'absolute',
                    color: 'common.white',
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    },
                  }}
                >
                  <Iconify icon="mingcute:close-line" width={14} />
                </IconButton>
              )}
            </Stack>
          );
        }
        return null;
      })}
    </AnimatePresence>
  ) : null;
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  onRemove: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
  onClick: PropTypes.func,
  setImageDialog: PropTypes.func,
  setValue: PropTypes.func,
  onRemoveAll: PropTypes.func,
};
